import Sticky from "react-stickynode"
import { useTranslation } from "next-i18next"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import dynamic from "next/dynamic"
import MetaData from "@/common/components/MetaData"
import { ProductBanner } from "@/common/components/ProductBanner"
import ProductCTA from "@/common/components/ProductCTA"
import { ProductNames } from "@/common/data/Products"
import SectionLine from "@/common/components/_UI/SectionLine"
import TransferDark from "@/common/assets/image/products/icons/TransferDark.svg"
import Card from "@/common/components/ProductCard/Card"
import Description from "@/common/components/Description"
import TransferSceneCards from "./TransferSceneCards"

import CrossBorderImage from "@/common/assets/image/products/transfer/Cross-border.png"
import builtInCompliance from "@/common/assets/image/products/transfer/Built-in-compliance.png"
import enterpriseGradeSecurity from "@/common/assets/image/products/transfer/Enterprise-grade-security.png"

const NavbarNew = dynamic(import("@/containers/common/NavbarNew"))

import { LayoutWrapper } from "@/common/components/LayoutStyle"
import Footer from "@/containers/common/FooterNew"
import CodeBlockContainer from "@/common/components/CodeBlockContainer"
import styled from "styled-components"
import { theme } from "@/common/Mixin"

const TransferPage = () => {
  const { t } = useTranslation(["transfer", "common"])

  const trustLogos = Array(5).fill(null)

  const simpleIntegrationCode = `const transfer = await KryptoGO.createTransfer({
  amount: '1000.00',
  currency: 'USDC',
  description: 'Order #1234 Transfer'
})`

  return (
    <>
      <MetaData
        title={t(
          "meta.title",
          "KryptoGO Transfer - Secure & Compliant Crypto Transfers"
        )}
        description={t(
          "meta.description",
          "Easily transfer virtual assets across blockchains with KryptoGO's secure and compliant API solution."
        )}
        url="https://www.kryptogo.com/products/transfer"
        image="/images/og-image.jpg"
        languageAlternates={[
          {
            hrefLang: "en",
            href: "https://www.kryptogo.com/products/transfer"
          },
          {
            hrefLang: "zh-TW",
            href: "https://www.kryptogo.com/zh-TW/products/transfer"
          },
          {
            hrefLang: "zh-CN",
            href: "https://www.kryptogo.com/zh-CN/products/transfer"
          }
        ]}
      />
      <Sticky enabled={true} top={0} innerZ={1000}>
        <NavbarNew />
      </Sticky>
      <LayoutWrapper>
        <ProductBanner
          bannerType={ProductNames.AssetPro}
          icon={TransferDark.src}
          title={t("Banner.productName")}
          titleh1={t("Banner.title")}
          description={t("Banner.description")}
          primaryButton={{
            content: t("CTA.button", "Contact Us"),
            arrowType: "none",
            href: "/contact-us",
            target: "_self"
          }}
        />
        <SectionLine />
        <div id="features" />

        <Description
          backgroundType="none"
          items={[
            {
              wide: true,
              titles: [{ text: t("Allyouneed.title"), textType: "h3" }]
            }
          ]}
        />

        <KGWrapper id="ux-cards" className="kg-wrapper">
          <CardRow>
            <Card
              backgroundType="darkLinear"
              slogan={t("Allyouneed.card1.title")}
              description={t("Allyouneed.card1.description")}
              image={CrossBorderImage}
            />
            <Card
              backgroundType="lightLinear"
              slogan={t("Allyouneed.card2.title")}
              description={t("Allyouneed.card2.description")}
              image={
                <CodeBlockContainer>
                  <pre style={{ margin: 0 }}>
                    <code>{simpleIntegrationCode}</code>
                  </pre>
                </CodeBlockContainer>
              }
            />
          </CardRow>

          <CardRow>
            <Card
              backgroundType="lightLinear"
              slogan={t("Allyouneed.card3.title")}
              description={t("Allyouneed.card3.description")}
              image={builtInCompliance}
            />
            <Card
              backgroundType="darkLinear"
              slogan={t("Allyouneed.card4.title")}
              description={t("Allyouneed.card4.description")}
              image={enterpriseGradeSecurity}
            />
          </CardRow>
        </KGWrapper>

        <KGWrapper style={{ marginTop: "2rem" }}>
          <Description
            backgroundType="none"
            items={[
              {
                wide: true,
                titles: [{ text: t("Scenario.title"), textType: "h3" }]
              }
            ]}
          />
          <TransferSceneCards />
        </KGWrapper>

        <ProductCTA
          title={t("CTA.title", "Ready to Transfer?")}
          primaryButton={{
            text: t("CTA.button", "Contact Us"),
            href: "/contact-us"
          }}
        />
        <Footer />
      </LayoutWrapper>
    </>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["transfer", "common"]))
    }
  }
}

export default TransferPage

const CardRow = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;

  @media (${theme.lg}) {
    flex-direction: column;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    > * {
      width: 100%;
    }
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`

const KGWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    gap: 0.75rem;
    padding: 0 0.75rem;
  }
`
